import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import type { GetLocationsInputDto, LocationCreateDto, LocationDto, LocationExcelDownloadDto, LocationGisCoordinateDto, LocationGisCoordinateUpdateResultDto, LocationUpdateDto } from '../dtos/location/models';
import type { DownloadTokenResultDto, GetByIdsInputDto } from '../dtos/shared/models';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private restService = inject(RestService);
  apiName = 'Planning';
  

  create = (input: LocationCreateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, LocationDto>({
      method: 'POST',
      url: '/api/planning/locations',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: number, checkDependencies: boolean = true, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/planning/locations/${id}`,
      params: { checkDependencies },
    },
    { apiName: this.apiName,...config });
  

  deleteByIds = (ids: number[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: '/api/planning/locations',
      params: { ids },
    },
    { apiName: this.apiName,...config });
  

  ensureActive = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/planning/locations/${id}/ensure-active`,
    },
    { apiName: this.apiName,...config });
  

  exists = (name: string, locationTypeId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, boolean>({
      method: 'GET',
      url: '/api/planning/locations/exists',
      params: { name, locationTypeId },
    },
    { apiName: this.apiName,...config });
  

  get = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, LocationDto>({
      method: 'GET',
      url: `/api/planning/locations/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getDownloadToken = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, DownloadTokenResultDto>({
      method: 'GET',
      url: '/api/planning/locations/download-token',
    },
    { apiName: this.apiName,...config });
  

  getList = (input: GetLocationsInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<LocationDto>>({
      method: 'GET',
      url: '/api/planning/locations',
      params: { name: input.name, locationTypeId: input.locationTypeId, locationTypeName: input.locationTypeName, locationTypeEntityName: input.locationTypeEntityName, isOrigin: input.isOrigin, isDestination: input.isDestination, latitude: input.latitude, latitudeFrom: input.latitudeFrom, latitudeTo: input.latitudeTo, longitude: input.longitude, longitudeFrom: input.longitudeFrom, longitudeTo: input.longitudeTo, area: input.area, areaFrom: input.areaFrom, areaTo: input.areaTo, isActive: input.isActive, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListAsExcelFile = (input: LocationExcelDownloadDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Blob>({
      method: 'GET',
      responseType: 'blob',
      url: '/api/planning/locations/as-excel-file',
      params: { downloadToken: input.downloadToken, fields: input.fields, name: input.name, locationTypeId: input.locationTypeId, locationTypeName: input.locationTypeName, locationTypeEntityName: input.locationTypeEntityName, isOrigin: input.isOrigin, isDestination: input.isDestination, latitude: input.latitude, latitudeFrom: input.latitudeFrom, latitudeTo: input.latitudeTo, longitude: input.longitude, longitudeFrom: input.longitudeFrom, longitudeTo: input.longitudeTo, area: input.area, areaFrom: input.areaFrom, areaTo: input.areaTo, isActive: input.isActive, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListByIds = (input: GetByIdsInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, LocationDto[]>({
      method: 'GET',
      url: '/api/planning/locations/by-ids',
      params: { ids: input.ids },
    },
    { apiName: this.apiName,...config });
  

  update = (id: number, input: LocationUpdateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, LocationDto>({
      method: 'PUT',
      url: `/api/planning/locations/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  updateGisCoordinates = (input: LocationGisCoordinateDto[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, LocationGisCoordinateUpdateResultDto>({
      method: 'PUT',
      url: '/api/planning/locations/gis-coordinates',
      body: input,
    },
    { apiName: this.apiName,...config });
}